import React, { useState, useEffect } from 'react';
import { Layout, Dropdown, Avatar, Space, Badge } from 'antd';
import {
  HomeOutlined,
  LineChartOutlined,
  CommentOutlined,
  LogoutOutlined,
  UserOutlined,
  AlertOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../assets/DM_Logo.svg';
import { apiFetch } from "../utils/api";
import { useSession } from '../context/SessionContext';
import { usePermissions } from '../hooks/usePermissions';
import { getActiveView } from '../utils/getActiveView';

const { Header } = Layout;

const hoverStyle = {
  transition: 'all 0.3s ease',
  padding: '0 4px',
  minWidth: 100,
  justifyContent: 'center'
};

function TopBar() {
  const [unconfirmedCount, setUnconfirmedCount] = useState(0);
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useSession();
  const {
    canViewUsers,
    canManageDevices,
    canManageEmissionPoints,
    canAccessHistoricalTab,
    canViewComplaints,
    canViewIncidents,
    isKunak
  } = usePermissions();

  useEffect(() => {
    setHoveredMenu(null);
  }, [location.pathname]);

  const activeView = getActiveView(location.pathname);

  const handleEnter = view => setHoveredMenu(view);
  const handleLeave = view => {
    setTimeout(() => {
      setHoveredMenu(current => (current === view ? null : current));
    }, 100);
  };

  const getStyle = (view, hasChildren = false) => {
    const isActive = activeView === view && !hasChildren;
    const isHovered = hoveredMenu === view || openMenu === view;
    return {
      ...hoverStyle,
      color: isActive ? '#0075C9' : '#000',
      backgroundColor: isHovered ? '#f5f5f5' : 'transparent',
      border: isHovered ? '1px solid #d9d9d9' : '1px solid transparent',
      borderRadius: 0,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      padding: '0 8px',
      textDecoration: 'none'
    };
  };

  const getUserStyle = () => ({
    ...getStyle('user'),
    marginRight: 16
  });

  const handleLogoutClick = async () => {
    try {
      await apiFetch('/api/logout', {
        method: 'POST',
        credentials: 'include'
      });
    } catch (err) {
      console.error("Logout failed", err);
    }
    logout();
    navigate('/login');
  };

  useEffect(() => {
    apiFetch('/api/warnings/unconfirmed-count')
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
    })
    .then(count => setUnconfirmedCount(count))
    .catch(err => console.error('Failed to load unconfirmed warnings count', err));
  }, []);

  return (
    <Header
      style={{
        background: '#fff',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: 64,
        borderBottom: '1px solid #e5e7eb'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{
          width: 260,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%'
        }}>
          <img src={logo} alt="Logo" style={{ height: 50 }} />
        </div>

        <Space size={0} style={{ fontSize: 16 }}>
          <span
            style={getStyle('map')}
            onMouseEnter={() => handleEnter('map')}
            onMouseLeave={() => handleLeave('map')}
            onClick={() => navigate('/map')}
          >
            <HomeOutlined /> Map
          </span>

          {canAccessHistoricalTab && (
            <span
              style={getStyle('readings')}
              onMouseEnter={() => handleEnter('readings')}
              onMouseLeave={() => handleLeave('readings')}
              onClick={() => navigate('/readings')}
            >
              <LineChartOutlined /> Historical
            </span>
          )}

          {canViewComplaints && (
            <span
              style={getStyle('complaints')}
              onMouseEnter={() => handleEnter('complaints')}
              onMouseLeave={() => handleLeave('complaints')}
              onClick={() => navigate('/complaints')}
            >
              <CommentOutlined /> Complaints
            </span>
          )}

          {canViewIncidents && (
            <span
              style={getStyle('incidents')}
              onMouseEnter={() => handleEnter('incidents')}
              onMouseLeave={() => handleLeave('incidents')}
              onClick={() => navigate('/incidents')}
            >
              <AlertOutlined /> Incidents
            </span>
          )}

          {canAccessHistoricalTab && (
            <span
              style={getStyle('warnings')}
              onMouseEnter={() => handleEnter('warnings')}
              onMouseLeave={() => handleLeave('warnings')}
              onClick={() => navigate('/warnings')}
            >
              <WarningOutlined />
              <span style={{ marginLeft: 4, position: 'relative' }}>
                Warnings
                {unconfirmedCount > 0 && (
                  <Badge
                    count={unconfirmedCount < 1000 ? unconfirmedCount : unconfirmedCount === 1000 ? "1K" : '+ 1K'}
                    style={{ backgroundColor: '#fa541c' }}
                    offset={[-10, -20]}
                    showZero={false}
                  />
                )}
              </span>
            </span>
          )}

        </Space>
      </div>

      <Dropdown
        trigger={['hover']}
        placement="bottomRight"
        onOpenChange={open => setOpenMenu(open ? 'user' : null)}
        menu={{
          items: [
            {
              key: 'edit-profile',
              label: <span style={{ color: activeView === 'edit-profile' ? '#0075C9' : '#000' }}>
                Edit Profile
              </span>,
              onClick: () => navigate('/edit-profile')
            },
            ...(canViewUsers ? [{
              key: 'users',
              label: <span style={{ color: activeView === 'users' ? '#0075C9' : '#000' }}>
                Users
              </span>,
              onClick: () => navigate('/users')
            }] : []),
            ...(canManageDevices ? [{
              key: 'devices',
              label: <span style={{ color: activeView === 'devices' ? '#0075C9' : '#000' }}>
                Devices
              </span>,
              onClick: () => navigate('/devices')
            }] : []),
            ...(canManageEmissionPoints ? [{
              key: 'emission-points',
              label: <span style={{ color: activeView === 'emission-points' ? '#0075C9' : '#000' }}>
                Emission points
              </span>,
              onClick: () => navigate('/emission-points')
            }] : []),
            ...(isKunak ? [{
              key: 'concentrations',
              // label: 'Test concentration',
              label: <span style={{ color: activeView === 'concentrations' ? '#0075C9' : '#000' }}>
                Test concentration
              </span>,
              onClick: () => navigate('/concentrations')
            }] : []),
            {
              key: 'logout',
              icon: <LogoutOutlined />,
              label: 'Logout',
              onClick: handleLogoutClick
            }
          ]
        }}
      >
        <div
          style={getUserStyle()}
          onMouseEnter={() => handleEnter('user')}
          onMouseLeave={() => handleLeave('user')}
        >
          <Avatar style={{ backgroundColor: '#0075C9' }} icon={<UserOutlined />} />
          <span style={{ fontWeight: '500', color: '#333' }}>{user}</span>
        </div>
      </Dropdown>
    </Header>
  );
}

export default TopBar;
